import type {
  RosterUploadSourceType,
  RosterUploadStatusFilter,
} from '../../../types/adminRosterUploads';
import { parsePage } from '../adminNavigation';

/**
 * The Uploaded Rosters list keeps its filters and page in the URL, so a per-organization view is a
 * link (`/admin/roster-uploads?org=<id>`) and browser back steps through filter changes.
 *
 * Everything read from the URL is untrusted: a hand-edited or stale link must not become a 400 from
 * the API (it binds `organizationId` as a Guid) or an unknown status sent to the server. Anything
 * unusable is dropped back to the default rather than passed through.
 */
export const ROSTER_UPLOADS_PATH = '/admin/roster-uploads';

export const STATUS_FILTERS: readonly RosterUploadStatusFilter[] = [
  'in_progress',
  'pending',
  'assessing',
  'assessed',
  'extracting',
  'completed',
  'failed',
];

/** Rows per page. Lives here, not in the data hook, because parsing the page bound needs it. */
export const ROSTER_UPLOADS_PAGE_SIZE = 25;

/**
 * The highest page the API can be asked for. It binds the page as an Int32 and multiplies it by the
 * page size to skip rows, so anything past this — `?page=3e9`, `?page=1e21` — is a 400 (or an
 * overflow) that no retry recovers from. Such a link falls back to the first page instead.
 */
export const MAX_ROSTER_UPLOADS_PAGE = Math.floor(2147483647 / ROSTER_UPLOADS_PAGE_SIZE);

const parseRosterUploadsPage = (raw: string | null): number => {
  const page = parsePage(raw);
  // `parsePage` has already made it a positive integer; the bound alone keeps it a safe one.
  return page <= MAX_ROSTER_UPLOADS_PAGE ? page : 1;
};

export const SOURCE_FILTERS: readonly RosterUploadSourceType[] = ['image', 'csv', 'excel_converted'];

export interface RosterUploadsFilters {
  organizationId: string;
  status: RosterUploadStatusFilter | '';
  sourceType: RosterUploadSourceType | '';
  search: string;
  page: number;
}

export const DEFAULT_FILTERS: RosterUploadsFilters = {
  organizationId: '',
  status: '',
  sourceType: '',
  search: '',
  page: 1,
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isGuid = (value: string): boolean => GUID_PATTERN.test(value);

const oneOfOrEmpty = <T extends string>(value: string | null, allowed: readonly T[]): T | '' =>
  value !== null && allowed.includes(value as T) ? (value as T) : '';

/** The list's filters as the URL states them, with anything unusable dropped. */
export const parseRosterUploadsParams = (params: URLSearchParams): RosterUploadsFilters => {
  const org = params.get('org') ?? '';
  return {
    organizationId: isGuid(org) ? org : '',
    status: oneOfOrEmpty(params.get('status'), STATUS_FILTERS),
    sourceType: oneOfOrEmpty(params.get('source'), SOURCE_FILTERS),
    search: (params.get('search') ?? '').trim(),
    page: parseRosterUploadsPage(params.get('page')),
  };
};

/** Only non-default values are written, so the default view is a clean `/admin/roster-uploads`. */
export const buildRosterUploadsParams = (filters: RosterUploadsFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.organizationId) params.set('org', filters.organizationId);
  if (filters.status) params.set('status', filters.status);
  if (filters.sourceType) params.set('source', filters.sourceType);
  const search = filters.search.trim();
  if (search) params.set('search', search);
  if (filters.page > 1) params.set('page', String(filters.page));
  return params;
};

/** The list URL for a set of filters. */
export const rosterUploadsHref = (filters: Partial<RosterUploadsFilters> = {}): string => {
  const qs = buildRosterUploadsParams({ ...DEFAULT_FILTERS, ...filters }).toString();
  return qs ? `${ROSTER_UPLOADS_PATH}?${qs}` : ROSTER_UPLOADS_PATH;
};

export const hasActiveFilters = (filters: RosterUploadsFilters): boolean =>
  Boolean(filters.organizationId || filters.status || filters.sourceType || filters.search);

/**
 * Where "Back to Uploaded Rosters" should land.
 *
 * The list passes its own URL as route state when an upload is opened, so going back restores its
 * filters and page. Route state survives a refresh and can be hand-edited, so only the list itself
 * (`/admin/roster-uploads` or `/admin/roster-uploads?…`) is accepted; anything else — another admin
 * page, a protocol-relative `//host`, a lookalike prefix — falls back to the bare list.
 */
export const resolveRosterUploadsBackTarget = (state: unknown): string => {
  if (typeof state !== 'object' || state === null) return ROSTER_UPLOADS_PATH;

  const from = (state as { from?: unknown }).from;
  if (typeof from !== 'string') return ROSTER_UPLOADS_PATH;

  if (from === ROSTER_UPLOADS_PATH) return from;
  if (!from.startsWith(`${ROSTER_UPLOADS_PATH}?`)) return ROSTER_UPLOADS_PATH;
  // No fragment tricks or backslashes, which some browsers normalise into a path separator.
  if (from.includes('\\') || from.includes('#')) return ROSTER_UPLOADS_PATH;

  return from;
};
